// Full credits-usage breakdown: balance, lifetime used, lifetime purchased,
// last-30-day spend with a tiny visual bar. Used on Profile (mobile + desktop).
import { Coins, TrendingDown, TrendingUp, ShoppingBag, CalendarDays } from "lucide-react";
import { useCreditUsage } from "@/hooks/useCreditUsage";
import { formatCredits } from "@/lib/format-credits";
import { cn } from "@/lib/utils";

interface Props {
  onBuyCredits?: () => void;
  className?: string;
}

const Stat = ({
  icon,
  label,
  value,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "default" | "primary" | "warning" | "success";
}) => {
  const toneClass =
    tone === "primary"
      ? "bg-primary/10 text-primary ring-primary/20"
      : tone === "warning"
        ? "bg-warning/10 text-warning ring-warning/20"
        : tone === "success"
          ? "bg-success/10 text-success ring-success/20"
          : "bg-white/[0.04] text-muted-foreground ring-white/10";
  return (
    <div className="flex flex-col items-start gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
      <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg ring-1", toneClass)}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="font-display text-lg font-bold tabular-nums leading-tight text-foreground">
          {value}
        </p>
        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>
      </div>
    </div>
  );
};

export const CreditsUsageCard = ({ onBuyCredits, className }: Props) => {
  const { balance, lifetimeSpent, lifetimePurchased, lifetimeGranted, spent30d, loading } =
    useCreditUsage();

  // % used = spent / everything ever credited (purchases + free grants).
  // Using `purchased + balance` would double-count balance and ignore free
  // signup/refund/ad credits, producing >100% for active users.
  const lifetimeIn = lifetimePurchased + lifetimeGranted;
  const usedRatio = lifetimeIn > 0 ? Math.min(1, lifetimeSpent / lifetimeIn) : 0;
  const usedPct = Math.round(usedRatio * 100);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-white/10 bg-card/40 p-4 backdrop-blur-sm",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"
      />
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
              Credit Usage
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Track your balance, spend and purchases.
            </p>
          </div>
          {onBuyCredits && (
            <button
              type="button"
              onClick={onBuyCredits}
              className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/20"
            >
              Top up
            </button>
          )}
        </div>

        {/* Balance + progress */}
        <div className="mt-4 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 p-3 ring-1 ring-primary/20">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl font-bold tabular-nums text-foreground">
                {loading ? "—" : formatCredits(balance)}
              </span>
              <span className="text-xs text-muted-foreground">credits left</span>
            </div>
            {lifetimeIn > 0 && (
              <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
                {usedPct}% used
              </span>
            )}
          </div>
          {lifetimeIn > 0 && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width] duration-500"
                style={{ width: `${usedPct}%` }}
              />
            </div>
          )}
        </div>

        {/* Stat grid */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat
            icon={<TrendingDown className="h-4 w-4" strokeWidth={2.4} />}
            label="Lifetime used"
            value={formatCredits(lifetimeSpent)}
          />
          <Stat
            icon={<ShoppingBag className="h-4 w-4" strokeWidth={2.4} />}
            label="Purchased"
            value={formatCredits(lifetimePurchased)}
            tone="success"
          />
          <Stat
            icon={<CalendarDays className="h-4 w-4" strokeWidth={2.4} />}
            label="Last 30d"
            value={formatCredits(spent30d)}
            tone="primary"
          />
        </div>
      </div>
    </div>
  );
};
